import { MouseEvent } from "react";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowsRotate } from "@fortawesome/free-solid-svg-icons";
import { BaseLogo, LogoType } from "../../../core/models/entities/BaseLogo";
import { Role } from "../../../core/models/entities/Role";
import { useProfileContext } from "../../../contexts/ProfileContext";
import { useOrganizationImpersonationContext } from "../../../contexts/OrganizationImpersonationContext";
import { enviroment } from "../../../enviroment";
import { useLogoPriceRefreshNow } from "../../../hooks/useLogoPriceRefreshNow";
import {
  isLogoProductPending,
  isProductPendingStale,
} from "../../../utils/functions/productPending";

// Lifecycle of the popup's background GetLogo (LogoDetailStatus in
// logo-details.component) - a local alias so this leaf component does not
// import from a screen.
type DetailStatus = "loading" | "ready" | "error";

interface Props {
  logo: BaseLogo;
  detailStatus?: DetailStatus;
  className?: string;
}

// The small "refresh prices now" icon button in the logo popup's price
// column (flag REACT_APP_LOGO_PRICE_REFRESH_NOW_ENABLED): admin only, image
// logos only (the API's worker handles nothing else and answers 409 for the
// rest). One click = ONE POST product-refresh (useLogoPriceRefreshNow), then
// locked for the API's cooldown with a native tooltip saying when it opens
// again (or "daily limit reached" when the API's global cap is the lock);
// locked as well while the logo is FRESHLY "Prices on their way"
// (isLogoProductPending with a stamp younger than the 10-minute stale line -
// the API's 409 alreadyPending would refuse it anyway) and while the popup's
// background GetLogo has not landed (detailStatus "loading" or "error") -
// the cache patch after the answer must hit a real detail entry, never the
// list stub, so the pending badge appears and the pending poll is armed. A
// STALE pending logo (badge "Prices delayed": the worker poisoned or the
// sweep is on it) is the one case the admin must be able to re-drive, so
// the button stays open there and the API answers 202 with a new stamp.
// Flag off = nothing rendered (no hook, no subscription): the DOM stays
// byte-identical.
export function PriceRefreshNowButton(props: Props) {
  if (!enviroment.logoPriceRefreshNowEnabled) return null;
  return <PriceRefreshNowButtonInner {...props} />;
}

function PriceRefreshNowButtonInner({ logo, detailStatus, className }: Props) {
  const { t } = useTranslation();
  const { role } = useProfileContext();
  // Act-as: the profile (and its role) is the impersonated vendor's, while
  // the token the API judges (WithOrganizationAccess -> user.IsAdmin; the
  // route reads no actAsOrgId) is still the admin's. Impersonation state
  // can only be held by an admin (/profile/{orgId} is admin-only and the
  // profile provider drops a failed act-as), so an active act-as counts as
  // admin here - the same rule LoggedInMenu applies to its act-as entries.
  const { organizationId: impersonatedOrgId } =
    useOrganizationImpersonationContext();
  const { state, lockKind, retryInSeconds, refresh } =
    useLogoPriceRefreshNow(logo);
  if (role !== Role.Admin && impersonatedOrgId == null) return null;
  if (logo.logoType !== LogoType.ImageLogo) return null;

  const pending =
    isLogoProductPending(logo) &&
    !isProductPendingStale(logo.productPendingSinceUtc);
  const loading = state === "loading";
  // Undefined = the caller has no background GetLogo (a later editor
  // drop-in): nothing to wait for.
  const detailNotReady = detailStatus !== undefined && detailStatus !== "ready";
  const disabled = state !== "idle" || pending || detailNotReady;
  const title = pending
    ? t("compLogoDesign.priceRefreshAlreadyPending")
    : state === "cooldown"
      ? lockKind === "dailyCap"
        ? t("compLogoDesign.priceRefreshDailyCap")
        : t("compLogoDesign.priceRefreshTryAgain", { n: retryInSeconds })
      : t("compLogoDesign.refreshPricesNow");

  // A row click on the logo list opens the detail: the click must stop here.
  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    refresh();
  };

  // The tooltip sits on the wrapper: a disabled button swallows hover in
  // some browsers, so the button lets pointer events through while disabled.
  return (
    <span
      title={title}
      onClick={(e) => e.stopPropagation()}
      className={`inline-flex items-center ${
        disabled ? "cursor-not-allowed" : ""
      } ${className ?? ""}`}
    >
      <button
        type="button"
        aria-label={title}
        aria-busy={loading}
        data-state={state}
        data-testid="price-refresh-now-button"
        disabled={disabled}
        onClick={handleClick}
        className="p-1 rounded text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors cursor-pointer disabled:opacity-40 disabled:pointer-events-none"
      >
        <FontAwesomeIcon icon={faArrowsRotate} className="w-3 h-3" spin={loading} />
      </button>
    </span>
  );
}
